import React, { useEffect, useState } from "react";
import { Autocomplete, Box, IconButton, InputAdornment, LinearProgress, TextField, Tooltip, Typography } from "@mui/material";
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import EditOffOutlinedIcon from '@mui/icons-material/EditOffOutlined';
import { Form, FormikErrors, FormikTouched } from "formik";
import { Map, useMap, Marker } from 'react-mapkit';
import ReactCountryFlag from "react-country-flag";
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import { AppColor, DEFAULT_MAP_LOCATION, Fonts } from "../../../../types/Constants";
import { Coordinate, GeocoderResponse, Place } from "../../../../types/models/Mapkit";
import { getCategoryColor } from '../../../../utils/utils';
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { fetchError } from "../../../../redux/actions";
import { BeaconDialogModes } from "./BeaconDialog";

interface BeaconPlacementFormProps {

  mode?: typeof BeaconDialogModes[number],
  values: MakeBeacon,
  errors: FormikErrors<MakeBeacon>,
  touched: FormikTouched<MakeBeacon>,
  handleChange: (e: React.ChangeEvent<any>) => void,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>,
  setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void,
  setCurrentCoordinate: (coordinate: Coordinate) => void
}

const BeaconPlacementForm: React.FC<BeaconPlacementFormProps> = ({ mode, values, errors, touched, handleChange, setFieldValue, setFieldTouched, setCurrentCoordinate }) => {

  const dispatch = useAppDispatch();
  const defaultLocation: Coordinate = DEFAULT_MAP_LOCATION; // San Francisco
  const { map, mapkit, mapProps } = useMap();

  const [currentPinCoordinate, setCurrentPinCoordinate] = useState<Coordinate | undefined>(values.latitude && values.longitude ? { latitude: values.latitude, longitude: values.longitude } : undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lookupAddress, setLookupAddress] = useState<string>();
  const [selectedLookupAddressOption, setSelectedLookupAddressOption] = useState<Place | null>(null);
  const [lookupAddressOptions, setLookupAddressOptions] = useState<Place[]>([]);
  const [userLocation, setUserLocation] = useState<Coordinate>();

  const categoryGroupState = useAppSelector((state) => state.category);

  useEffect(() => {

    if (mode !== "EDIT" && !values.name && !currentPinCoordinate) {
      // New beacon && no beacon's name input && not called from map
      setIsLoading(true);
      initialisePinWithCurrentLocation(() => setIsLoading(false));
    } else {
      // Get current location for the current location button
      initialiseUserLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading) {
      addMapEventListener();

      if (mode === "EDIT" || currentPinCoordinate) {
        fitToMarkers(currentPinCoordinate);
      }
      else {
        if (values.name && mapkit) {
          setIsLoading(true);
          // Search location by beacon's name
          var geocoder = new mapkit.Geocoder();
          geocoder.getsUserLocation = true; // Lookup from current location
          geocoder.lookup(values.name, (error: Error | null, data: GeocoderResponse) => {
            if (error) {
              console.log("Failed to lookup with beacon's name:" + values.name);
            } else if (data && data.results && data.results.length > 0) {
              // Finish initialisation if the beacon's name hit a location
              updateFormikCoordinateValues(data.results[0].coordinate);
              setIsLoading(false);
              // Get current location for the current location button
              initialiseUserLocation();
              return;
            }
            // Set pin with current user's location if failed/not found the look up
            initialisePinWithCurrentLocation(() => setIsLoading(false));
          });
        }
      }
      return () => { removeMapEventListener(); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (lookupAddress === undefined) return;

    if (map) {
      const delayFunction = setTimeout(() => {
        if (lookupAddress) {
          setIsLoading(true);
          var geocoder = new mapkit.Geocoder();
          geocoder.getsUserLocation = true; // Lookup from current location
          geocoder.lookup(lookupAddress, (error: Error | null, data: GeocoderResponse) => {
            setIsLoading(false);

            if (error) {
              dispatch(fetchError("Failed to lookup"));
              return;
            }

            if (data && data.results && data.results.length > 0) {
              setLookupAddressOptions(data.results);
              const { coordinate } = data.results[0];
              fitToMarkers(coordinate);
            }
          });
        } else if (currentPinCoordinate) {
          fitToMarkers(currentPinCoordinate);
        }
      }, 1000); // Start looking up the map with delay to wait for user's input

      return () => clearTimeout(delayFunction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupAddress]);

  useEffect(() => {
    if (values.latitude && values.longitude) {
      const coordinate: Coordinate = { latitude: values.latitude, longitude: values.longitude };
      setCurrentPinCoordinate(coordinate);
      setCurrentCoordinate(coordinate);
      const delayFunction = setTimeout(() => {
        replaceAnnotation(coordinate);
      }, 500); // Start looking up the map with delay to wait for user's input

      return () => clearTimeout(delayFunction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.latitude, values.longitude]);

  const initialisePinWithCurrentLocation = (callBack: () => void) => {

    navigator.geolocation.getCurrentPosition((position) => {
      updateFormikCoordinateValues(position.coords);
      setUserLocation(position.coords);
      callBack();
    }, (positionError) => {
      console.log(`Using a default location because failed to get current position. Code:${positionError.code} Message:${positionError.message}`);
      // Set San Francisco
      updateFormikCoordinateValues(defaultLocation);
      callBack();
    });
  };

  const initialiseUserLocation = () => {
    try {
      navigator.geolocation.getCurrentPosition((position) => {
        setUserLocation(position.coords);
      });
    } catch (err) {
      console.log(err);
    }
  };

  function replaceAnnotation(coordinate: Coordinate) {
    if (map) {
      map.removeAnnotations(map.annotations);
      const mapkitCoordinate = new mapkit.Coordinate(coordinate.latitude, coordinate.longitude);
      map.addAnnotation(new mapkit.MarkerAnnotation(mapkitCoordinate, { draggable: true, color: getCategoryColor(values.category ?? "", categoryGroupState.categories ?? []), title: values.name }));
    }
  }

  function moveCurrentMapPosition(coordinate: Coordinate, zoom: number) {
    if (map) {
      const center = new mapkit.Coordinate(coordinate.latitude, coordinate.longitude);
      const span = new mapkit.CoordinateSpan(zoom, zoom);
      const region = new mapkit.CoordinateRegion(center, span);
      map.setRegionAnimated(region);
    }
  }

  function fitToMarkers(coordinate?: Coordinate) {
    // Move map area and adjust zoom to display pins
    if (map && coordinate) {
      moveCurrentMapPosition(coordinate, 0.03);
    }
  }

  function setDefaultRegion() {
    if (map) {
      moveCurrentMapPosition(defaultLocation, 600.0);
    }
  }

  function addMapEventListener() {
    if (map && !(mode == "MOD_ONLY" || mode == "READ_ONLY")) {
      map.addEventListener("long-press", handlePinSelect);
      map.addEventListener("drag-end", handleDragEnd);
      return () => removeMapEventListener();
    }
  }

  function removeMapEventListener() {
    if (map) {
      map.removeEventListener("long-press", handlePinSelect);
      map.removeEventListener("drag-end", handleDragEnd);
    }
  }

  const updateFormikCoordinateValues = (coordinate: Coordinate) => {
    setFieldValue("latitude", coordinate.latitude);
    setFieldValue("longitude", coordinate.longitude);
    setTimeout(() => setFieldTouched("latitude", false));
    setTimeout(() => setFieldTouched("longitude", false));
  };

  const getDefaultBeacon = () => {
    if (currentPinCoordinate) {
      return (
        <Marker
          latitude={currentPinCoordinate.latitude}
          longitude={currentPinCoordinate.longitude}
          title={values.name}
          color={getCategoryColor(values.category ?? "", categoryGroupState.categories ?? [])}
          draggable
        />
      );
    }
    else {
      setDefaultRegion();
      return undefined;
    }
  };

  const handlePinSelect = (event: any) => {
    const mapkitCoordinate = map.convertPointOnPageToCoordinate(event.pointOnPage);
    const newCoordinate: Coordinate = { latitude: mapkitCoordinate.latitude, longitude: mapkitCoordinate.longitude };
    setCurrentPinCoordinate(newCoordinate);
    updateFormikCoordinateValues(newCoordinate);
    replaceAnnotation(newCoordinate);
    reverseGeocodeAndSetAddress(mapkitCoordinate);
  };

  const handleDragEnd = (event: any) => {
      const newCoordinate = event.annotation.coordinate;
      setCurrentPinCoordinate(newCoordinate);
      updateFormikCoordinateValues(newCoordinate);
      replaceAnnotation(newCoordinate);
      reverseGeocodeAndSetAddress(newCoordinate);
  };

  const reverseGeocodeAndSetAddress = (coordinate: Coordinate) => {

    if (!mapkit) return;
  
    const geocoder = new mapkit.Geocoder();
  
    geocoder.reverseLookup(coordinate, (error: Error | null, data: GeocoderResponse) => {
      if (error) {
        dispatch(fetchError("Failed to retrieve address")); 
        return;
      }
  
      if (data?.results?.length > 0) {
        const address = data.results[0];
        setLookupAddress(address.formattedAddress);
        setSelectedLookupAddressOption(address);
      }
    });
  };

  return (
    <Form style={{ width: "100%" }}>
      <Box sx={{ marginX: 2, display: "flex", alignItems: "flex-end" }}>
        <Autocomplete
          disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
          fullWidth
          id="address"
          noOptionsText="No locations"
          filterOptions={(options) => options}
          loading={isLoading}
          value={selectedLookupAddressOption}
          getOptionLabel={(option) => option.formattedAddress ?? option.name}
          onKeyDown={e => {
            e.key === 'Enter' && e.preventDefault();
          }}
          onChange={(event, value, reason) => {
            switch (reason) {
              case "selectOption":
                if (value) {
                  setSelectedLookupAddressOption(value);
                  // Set current pin to selected location
                  updateFormikCoordinateValues(value.coordinate);
                }
                break;
              case "clear":
                setSelectedLookupAddressOption(null);
                setLookupAddressOptions([]);
                break;
              default:
                break;
            }
          }}
          renderOption={(props, option) => {
            return (
              <Box component="li" {...props} sx={{ fontSize: 14 }} key={option.coordinate.latitude + option.coordinate.longitude}>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ReactCountryFlag
                      countryCode={option.countryCode}
                      svg
                      style={{
                        marginLeft: "0px",
                        marginRight: "8px",
                        marginBottom: "0px",
                        width: '16px',
                        height: '16px',
                      }}></ReactCountryFlag>
                    <Typography sx={{ mr: 2 }} fontWeight={Fonts.SEMI_BOLD}>{option.name}</Typography>
                  </Box>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: '2',
                      WebkitBoxOrient: 'vertical'
                    }}>{option.formattedAddress}</Typography>
                </Box>
              </Box>);
          }

          }
          renderInput={(params) => (
            <TextField {...params}
              disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
              label="Search"
              variant="standard"
              value={lookupAddress ?? ""}
              onChange={(event) => setLookupAddress(event.target.value)}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          )}
          options={lookupAddressOptions} />
        {userLocation &&
          <Tooltip title="Show your current location">
            <IconButton
              size="small"
              sx={{ height: 24, width: 24, ml: 2, mb: 1 }}
              onClick={() => {
                if (map && userLocation) {
                  moveCurrentMapPosition(userLocation, 0.03);
                  setLookupAddress(undefined);
                  setSelectedLookupAddressOption(null);
                  setLookupAddressOptions([]);
                }
              }}>
              <NearMeOutlinedIcon />
            </IconButton>
          </Tooltip>
        }
      </Box>
      <Box sx={{
        height: "4px",
        marginTop: 1,
        marginLeft: 2
      }}>
        {isLoading && <LinearProgress color="secondary" />}
      </Box>
      <Box sx={{ marginLeft: 2, height: "calc(70vh - 130px)" }}>
        <Map {...mapProps}>
          {getDefaultBeacon()}
        </Map>
      </Box>
      <Box>
        <Typography sx={{ color: AppColor.subHeaderText, fontSize: 12, ml: 2 }}>To place a pin, place the cursor on the location, then click and hold the left mouse key.</Typography>
      </Box>
    </Form>
  );
};

export default BeaconPlacementForm;
